import { Avatar } from '@mui/material';
import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from './utils/axios-service';
import { useEffect, useState } from 'react';
import { Colors } from './utils/avatar-colors';
import { useAppSelector } from '../../redux/hooks';
import { useDispatch } from 'react-redux';
import { profilePictureActions } from '../../redux/profilePicture/slice';

interface Props {
  providerId: string;
  width?: string;
  height?: string;
  alt?: string;
  fontSize?: string;
}

interface IColorsAvatar {
  backgroundColor: string;
  color: string;
}

const AvatarImage = ({
  providerId,
  width,
  height,
  alt,
  fontSize
}: Props): JSX.Element => {
  const [profilePic, setProfilePic] = useState<string | undefined>(undefined);
  const [initialsValue, setInitialsValue] = useState<string>();
  const [avatarColors, setAvatarColors] = useState({
    backgroundColor: '',
    color: ''
  });

  const profilePictures = useAppSelector(
    (state) => state.profilePicture.profilePictures
  );
  const dispatch = useDispatch();

  const getProfilePic: (id: string) => Promise<AxiosResponse> = async (
    id: string
  ): Promise<AxiosResponse> => {
    try {
      return await axiosInstance.get(`fetchProfilePicture?userId=${id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 502) {
          setProfilePic('');
        }
        throw error;
      } else {
        throw error;
      }
    }
  };

  const arrayBufferToDataURL = (
    arrayBuffer: ArrayBuffer,
    mimeType: string
  ): string => {
    const base64String = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(arrayBuffer) as unknown as number[]
      )
    );
    return `data:${mimeType};base64,${base64String}`;
  };

  useEffect(() => {
    const fetchProfilePic = async (): Promise<void> => {
      try {
        for (let i = 0; i < profilePictures.length; i++) {
          if (
            profilePictures[i].providerId === providerId &&
            Number(profilePictures[i].fetchedTime) + 24 * 60 * 60 * 1000 >=
              Date.now()
          ) {
            return setProfilePic(profilePictures[i].dataUrl);
          }
        }

        const profilePicRes = await getProfilePic(providerId);
        const mimeType = 'image/jpg';
        if (profilePicRes.data.profilePic.data[0]) {
          const dataURL = arrayBufferToDataURL(
            profilePicRes.data.profilePic.data,
            mimeType
          );
          dispatch(
            profilePictureActions.setProfileData({
              dataUrl: dataURL,
              providerId,
              fetchedTime: Date.now().toString()
            })
          );
          setProfilePic(dataURL);
        } else {
          dispatch(
            profilePictureActions.setProfileData({
              dataUrl: '',
              providerId,
              fetchedTime: Date.now().toString()
            })
          );
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setProfilePic('');
        } else {
          dispatch(
            profilePictureActions.setProfileData({
              dataUrl: '',
              providerId,
              fetchedTime: Date.now().toString()
            })
          );
          throw error;
        }
      }
    };
    void fetchProfilePic();
  }, [providerId]);

  useEffect(() => {
    const firstTwoWords = alt ? alt.split(' ', 2) : [];
    setInitialsValue(
      firstTwoWords.map((word) => word.charAt(0).toUpperCase()).join('')
    );

    const getRandomColor = (): IColorsAvatar => {
      const randomIndex = Math.floor(Math.random() * Colors.length);
      return Colors[randomIndex];
    };

    const randomColors = getRandomColor();
    setAvatarColors(randomColors);
  }, [alt]);

  return (
    <>
      {!profilePic ? (
        <Avatar
          sx={{
            width,
            height,
            fontWeight: 500,
            fontSize: !fontSize
              ? width && height
                ? '50px'
                : '10px'
              : fontSize,
            color: !profilePic && alt ? avatarColors.color : 'white',
            backgroundColor:
              !profilePic && alt ? avatarColors.backgroundColor : '#BDBDBD'
          }}
        >
          {initialsValue}
        </Avatar>
      ) : (
        <Avatar
          key={providerId}
          src={profilePic}
          sx={{
            width,
            height
          }}
        />
      )}
    </>
  );
};

export default AvatarImage;
